<template>
  <div class="gxy-site-page">
    <header class="page-header">
      <h1>添加公学芸账号</h1>
    </header>

    <main class="page-content">
      <img
        class="gxy-logo"
        src="./assets/gongxueyun-logo.png"
        alt="公学芸"
      />

      <form class="account-form" @submit.prevent="onSubmit">
        <label class="field">
          <span>账号</span>
          <input
            v-model.trim="form.phone"
            type="text"
            inputmode="text"
            autocomplete="username"
            placeholder="请输入账号或手机号"
            :disabled="loading"
          />
        </label>

        <label class="field">
          <span>密码</span>
          <input
            v-model="form.password"
            type="password"
            autocomplete="current-password"
            placeholder="请输入密码"
            :disabled="loading"
          />
        </label>

        <label class="field province-field">
          <span>打卡省份</span>
          <select ref="provinceSelect" v-model="form.province" :disabled="loading" aria-label="打卡省份">
            <option value="" disabled>请选择打卡省份</option>
            <option v-for="province in provinces" :key="province" :value="province">{{ province }}</option>
          </select>
        </label>

        <button class="submit-button" type="submit" :disabled="loading">
          {{ waitingForProxy ? '正在获取网络...' : (loading ? '正在添加...' : '登录并添加') }}
        </button>
        <p v-if="waitingForProxy" class="proxy-wait-message" role="status">正在获取网络，请保持页面打开，稍后会自动继续。</p>
      </form>

      <nav class="tutorial-links" aria-label="公学芸使用教程">
        <button
          v-for="tutorial in tutorials"
          :key="tutorial.id"
          type="button"
          :class="{ active: activeTutorial === tutorial.id }"
          @click="toggleTutorial(tutorial.id)"
        >
          {{ tutorial.title }}
        </button>
      </nav>

      <transition name="tutorial">
        <section v-if="selectedTutorial" class="tutorial-content">
          <h2>{{ selectedTutorial.title }}</h2>
          <ol>
            <li v-for="step in selectedTutorial.steps" :key="step">{{ step }}</li>
          </ol>
        </section>
      </transition>
    </main>

    <BottomNavigation :currentRoute="$route.path" />
  </div>
</template>

<script>
import axios from 'axios'
import { Toast } from 'vant'
import BottomNavigation from '@/components/BottomNavigation.vue'
import { API_ENDPOINTS, TIMEOUT_CONFIG } from '@/config/api'
import GXY_PROVINCES from '@/config/gxyProvinces'
import { createAccountProxyOperation, waitForAccountProxy } from '@/utils/accountProxyWait'

export default {
  name: 'WwwGxyHome',
  components: {
    BottomNavigation
  },
  data() {
    return {
      form: {
        phone: '',
        password: '',
        province: ''
      },
      provinces: [...GXY_PROVINCES].sort((a, b) => a.localeCompare(b, 'zh-Hans-CN')),
      loading: false,
      waitingForProxy: false,
      accountProxyOperation: null,
      activeTutorial: null,
      tutorials: [
        {
          id: 'account',
          title: '添加账号教程',
          steps: [
            '输入公学芸账号和密码，选择打卡省份。',
            '点击“登录并添加”，等待账号验证。',
            '添加成功后会自动进入账号列表。'
          ]
        },
        {
          id: 'signin',
          title: '签到设置教程',
          steps: [
            '在账号列表中选择已添加的公学芸账号。',
            '设置签到地点、上下班时间和签到日期。',
            '保存设置后，可在账号列表查看签到状态。'
          ]
        }
      ]
    }
  },
  computed: {
    selectedTutorial() {
      return this.tutorials.find(item => item.id === this.activeTutorial) || null
    }
  },
  mounted() {
    window.addEventListener('pagehide', this.stopAccountProxyWaiting)
    if (!localStorage.getItem('token')) {
      this.$router.replace('/lanhu_yonghudenglu')
    }
  },
  beforeRouteLeave(to, from, next) {
    this.stopAccountProxyWaiting()
    next()
  },
  beforeDestroy() {
    this.stopAccountProxyWaiting()
    window.removeEventListener('pagehide', this.stopAccountProxyWaiting)
  },
  methods: {
    stopAccountProxyWaiting() {
      if (!this.accountProxyOperation) return
      this.accountProxyOperation = null
      this.waitingForProxy = false
      this.loading = false
    },
    promptProvinceSelection() {
      this.form.province = ''
      Toast('请选择打卡省份后再提交')
      this.$nextTick(() => {
        const select = this.$refs.provinceSelect
        if (select) select.focus()
      })
    },
    toggleTutorial(id) {
      this.activeTutorial = this.activeTutorial === id ? null : id
    },
    async onSubmit() {
      if (this.loading) return
      if (!this.form.phone || !this.form.password) {
        Toast('请输入账号和密码')
        return
      }
      if (!this.form.province) {
        this.promptProvinceSelection()
        return
      }

      const token = localStorage.getItem('token')
      if (!token) {
        this.$router.replace('/lanhu_yonghudenglu')
        return
      }

      let operation = null
      try {
        this.loading = true
        const payload = {
          phone: this.form.phone,
          password: this.form.password,
          province: this.form.province
        }
        operation = createAccountProxyOperation()
        this.accountProxyOperation = operation
        const res = await waitForAccountProxy(op => axios.post(API_ENDPOINTS.GXY.ADD_USER, {
          ...payload, operation_id: op.id, operation_step: op.step
        }, {
          headers: { Authorization: `Bearer ${token}` }, timeout: TIMEOUT_CONFIG.GXY_ADD_ACCOUNT
        }), operation, {
          isCurrent: () => this.accountProxyOperation === operation,
          onWait: () => { this.waitingForProxy = true }
        })
        if (!res || this.accountProxyOperation !== operation) return
        this.waitingForProxy = false

        if (res.data && Number(res.data.code) === 20003) {
          this.promptProvinceSelection()
          return
        }
        if (res.data && res.data.code === 0) {
          Toast.success(res.data.message || '添加成功')
          this.$router.push('/lanhu_zhanghaoguanli')
          return
        }

        Toast.fail((res.data && res.data.message) || '绑定失败')
      } catch (error) {
        if (operation && this.accountProxyOperation !== operation) return
        if (error.response && error.response.data && Number(error.response.data.code) === 20003) {
          this.promptProvinceSelection()
          return
        }
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token')
          this.$router.replace('/lanhu_yonghudenglu')
          return
        }
        const message = error.response && error.response.data && error.response.data.message
        Toast.fail(message || '连接中断，添加结果暂未确认，请先查看账号列表')
      } finally {
        if (!operation || this.accountProxyOperation === operation) {
          this.accountProxyOperation = null
          this.waitingForProxy = false
          this.loading = false
        }
      }
    }
  }
}
</script>

<style scoped>
.gxy-site-page {
  min-height: 100vh;
  background: #f6f7f8;
  color: #1f2329;
}

.page-header {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  background: #fff;
  border-bottom: 1px solid #eef0f2;
}

.page-header h1 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0;
}

.page-content {
  box-sizing: border-box;
  width: 100%;
  max-width: 620px;
  margin: 0 auto;
  padding: 44px 16px 112px;
}

.gxy-logo {
  display: block;
  width: 176px;
  max-width: 52vw;
  height: auto;
  margin: 0 auto 34px;
}

.account-form {
  box-sizing: border-box;
  width: 100%;
  padding: 20px;
  background: #fff;
  border: 1px solid #f0f1f2;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(31, 35, 41, 0.05);
}

.field {
  display: flex;
  align-items: center;
  min-height: 52px;
  margin-bottom: 14px;
  padding: 0 16px;
  background: #fafafa;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-sizing: border-box;
}

.field span {
  flex: 0 0 52px;
  color: #60656f;
  font-size: 15px;
}

.field input {
  min-width: 0;
  flex: 1;
  padding: 0;
  color: #1f2329;
  font-size: 15px;
  line-height: 52px;
  letter-spacing: 0;
  background: transparent;
  border: 0;
  outline: none;
}

.field input::placeholder {
  color: #b8bdc6;
}

.province-field span { flex-basis: 74px; }
.province-field select {
  min-width: 0;
  flex: 1;
  height: 52px;
  color: #1f2329;
  font-size: 15px;
  background: transparent;
  border: 0;
  outline: none;
}

.submit-button {
  width: 100%;
  height: 48px;
  margin-top: 8px;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
  background: #00c968;
  border: 0;
  border-radius: 24px;
  cursor: pointer;
}

.submit-button:active {
  background: #00b85f;
}

.submit-button:disabled {
  cursor: default;
  opacity: 0.65;
}
.proxy-wait-message { margin: 12px 0 0; color: #60656f; font-size: 13px; line-height: 1.6; }

.tutorial-links {
  display: flex;
  justify-content: center;
  gap: 34px;
  margin-top: 30px;
}

.tutorial-links button {
  padding: 6px 0;
  color: #1677ff;
  font-size: 15px;
  letter-spacing: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.tutorial-links button.active {
  color: #00a957;
}

.tutorial-content {
  box-sizing: border-box;
  width: 100%;
  margin-top: 14px;
  padding: 18px 20px;
  background: #fff;
  border-top: 2px solid #00c968;
}

.tutorial-content h2 {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
}

.tutorial-content ol {
  margin: 0;
  padding-left: 20px;
  color: #60656f;
  font-size: 14px;
  line-height: 1.8;
}

.tutorial-enter-active,
.tutorial-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.tutorial-enter,
.tutorial-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (min-width: 768px) {
  .page-header {
    height: 60px;
  }

  .page-header h1 {
    font-size: 20px;
  }

  .page-content {
    padding-top: 56px;
  }

  .account-form {
    padding: 24px;
  }
}

@media (max-width: 360px) {
  .page-content {
    padding-right: 12px;
    padding-left: 12px;
  }

  .account-form {
    padding: 16px;
  }

  .tutorial-links {
    gap: 22px;
  }
}
</style>
